import React, { useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import Header from "../Components/Header";

const CreateUser = () => {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    department_name: "",
    email_id: "",
    mobile_no: "",
    city: "",
    pincode: "",
    address: "",
    country: "",
    role: "user",
  });
  
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      // Create preview URL for the selected image
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Create FormData object for multipart/form-data submission
      const submitData = new FormData();
      
      // Add all form fields to FormData
      Object.keys(formData).forEach(key => {
        submitData.append(key, formData[key]);
      });
      
      // Add image if selected
      if (image) {
        submitData.append("image", image);
      }
      
      const response = await axios.post(
        "/api/create_user/", 
        submitData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      
      Swal.fire({
        title: "Success!",
        text: response.data.message,
        icon: "success",
        confirmButtonColor: "#3B82F6",
      });
      
      // Reset form
      setFormData({
        username: "",
        password: "",
        department_name: "",
        email_id: "",
        mobile_no: "",
        city: "",
        pincode: "",
        address: "",
        country: "",
        role: "user",
      });
      setImage(null);
      setImagePreview(null);
      
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error.response?.data?.detail || "Something went wrong",
        icon: "error",
        confirmButtonColor: "#EF4444",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Group form fields into logical sections
  const personalFields = ["username", "password", "email_id", "mobile_no", "role"];
  const addressFields = ["address", "city", "pincode", "country"];
  const workFields = ["department_name"];

  return (
    <>
    <Header/>
      <div className="max-w-4xl mx-auto mt-10 p-8 bg-white border rounded-2xl shadow-lg">
        <div className="mb-8 text-center">
          <h2 className="text-3xl font-bold text-gray-800">Create New User</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Personal Information Section */}
          <div className="bg-gray-50 p-6 rounded-xl">
            <h3 className="text-xl font-semibold text-gray-700 mb-4">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {personalFields.map((key) =>
                key === "role" ? (
                  <div key={key} className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1 capitalize">Role</label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition duration-200"
                      required
                    >
                      <option value="admin">Admin</option>
                      <option value="user">User</option>
                    </select>
                  </div>
                ) : (
                  <div key={key} className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1 capitalize">
                      {key.replace(/_/g, " ")}
                    </label>
                    <input
                      type={
                        key === "password"
                          ? "password"
                          : key === "email_id"
                          ? "email"
                          : key === "mobile_no"
                          ? "tel"
                          : "text"
                      }
                      name={key}
                      value={formData[key]}
                      onChange={handleChange}
                      className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition duration-200"
                      placeholder={`Enter ${key.replace(/_/g, " ")}`}
                      required
                    />
                  </div>
                )
              )}
            </div>
          </div>

          {/* Work Information Section */}
          <div className="bg-gray-50 p-6 rounded-xl">
            <h3 className="text-xl font-semibold text-gray-700 mb-4">Work Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {workFields.map((key) => (
                <div key={key} className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1 capitalize">
                    {key.replace(/_/g, " ")}
                  </label>
                  <input
                    type="text"
                    name={key}
                    value={formData[key]}
                    onChange={handleChange}
                    className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition duration-200"
                    placeholder={`Enter ${key.replace(/_/g, " ")}`}
                    required
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Address Information Section */}
          <div className="bg-gray-50 p-6 rounded-xl">
            <h3 className="text-xl font-semibold text-gray-700 mb-4">Address Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {addressFields.map((key) => (
                <div key={key} className={key === "address" ? "md:col-span-2" : "col-span-1"}>
                  <label className="text-sm font-medium text-gray-700 mb-1 capitalize">
                    {key.replace(/_/g, " ")}
                  </label>
                  <input
                    type={key === "pincode" ? "number" : "text"}
                    name={key}
                    value={formData[key]}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition duration-200"
                    placeholder={`Enter ${key.replace(/_/g, " ")}`}
                    required
                  />
                </div>
              ))}
            </div>
          </div>

          {/* New Image Upload Section */}
          <div className="bg-gray-50 p-6 rounded-xl">
            <h3 className="text-xl font-semibold text-gray-700 mb-4">Profile Image</h3>
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Upload Profile Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition duration-200"
                />
                <p className="mt-2 text-sm text-gray-500">
                  Accepted formats: JPG, PNG, GIF. Max size: 5MB
                </p>
              </div>
              
              <div className="flex-1 flex items-center justify-center">
                {imagePreview ? (
                  <div className="relative">
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="h-40 w-40 object-cover rounded-lg border-2 border-gray-300" 
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setImage(null);
                        setImagePreview(null);
                      }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 focus:outline-none"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="h-40 w-40 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-400">
                    <div className="text-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="mt-1">No image</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-center mt-8">
            <button
              type="submit"
              disabled={isLoading}
              className="bg-blue-600 text-white py-3 px-8 rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 transition duration-300 text-lg font-medium shadow-md flex items-center space-x-2"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>Processing...</span>
                </>
              ) : (
                <span>Create User</span>
              )}
            </button>
          </div>
        </form>
      </div>
      <div className="py-8 text-center text-gray-500">
        <p>Weather Station Management System © 2025</p>
      </div>
    </>
  );
};

export default CreateUser;